import clsx from 'clsx'
import { useOrderPanelStore } from '../../stores/order-panel-store'

const STEP = 3

export function RouteSelector() {
  const channel = useOrderPanelStore((s) => s.channel)
  const zelfBrengen = useOrderPanelStore((s) => s.zelfBrengen)
  const routes = useOrderPanelStore((s) => s.routes)
  const isLoading = useOrderPanelStore((s) => s.isLoadingRoutes)
  const error = useOrderPanelStore((s) => s.routesError)
  const buitenWerkgebied = useOrderPanelStore((s) => s.buitenWerkgebied)
  const visibleCount = useOrderPanelStore((s) => s.visibleRoutesCount)
  const showMore = useOrderPanelStore((s) => s.showMoreRoutes)
  const selectedRouteId = useOrderPanelStore((s) => s.selectedRouteId)
  const setSelectedRoute = useOrderPanelStore((s) => s.setSelectedRoute)
  const optimisticBoost = useOrderPanelStore((s) => s.optimisticCapacityBoost)
  const pendingBump = useOrderPanelStore((s) => s.pendingCapacityBump)
  const openCapacityBump = useOrderPanelStore((s) => s.openCapacityBump)

  // Only show for ORDER channel
  if (channel !== 'ORDER') return null
  // Klant brengt zelf — geen ophaal-rit nodig.
  if (zelfBrengen) return null

  if (isLoading) {
    return (
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-600 block">Ophaalmoment</label>
        <div className="space-y-2 animate-pulse" role="status" aria-label="Routes laden">
          <div className="h-12 bg-slate-100 rounded-md" />
          <div className="h-12 bg-slate-100 rounded-md" />
          <div className="h-12 bg-slate-100 rounded-md" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-600 block">Ophaalmoment</label>
        <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      </div>
    )
  }

  if (routes.length === 0) {
    return (
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-600 block">Ophaalmoment</label>
        {buitenWerkgebied ? (
          <div className="px-3 py-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
            Buiten werkgebied — geen automatische ophaalmomenten. De order komt op
            &ldquo;Ophalen handmatig&rdquo; te staan; plan de rit na het inboeken via de
            order met &ldquo;Andere route kiezen&rdquo;.
          </div>
        ) : (
          <div className="px-3 py-4 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">
            Geen ophaalroutes beschikbaar voor deze postcode
          </div>
        )}
      </div>
    )
  }

  const visible = routes.slice(0, visibleCount)
  const hiddenCount = Math.max(0, routes.length - visibleCount)
  const nextStep = Math.min(STEP, hiddenCount)

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-slate-600 block">Ophaalmoment</label>

      <div className="space-y-2">
        {visible.map((route) => {
          const isSelected = selectedRouteId === route.routeId
          const optimisticDelta =
            optimisticBoost?.routeId === route.routeId ? optimisticBoost.delta : 0
          const effectiveCapaciteit = route.capaciteit + optimisticDelta
          const isFullEffective = effectiveCapaciteit <= 0
          const isLowCapacity =
            !isFullEffective && effectiveCapaciteit > 0 && effectiveCapaciteit <= 3
          const isVandaag = route.isVandaag === true
          const isNietVoorstellen = route.isNietVoorstellen === true
          const hasPending = pendingBump?.routeId === route.routeId

          return (
            <label
              key={route.routeId}
              className={clsx(
                'flex items-start gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors',
                isSelected
                  ? 'border-indigo-500 bg-indigo-50'
                  : isVandaag
                    ? 'border-amber-300 bg-amber-50/60 hover:border-amber-400'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
              )}
            >
              <input
                type="radio"
                name="order-route"
                className="mt-1 accent-indigo-600"
                checked={isSelected}
                onChange={() => setSelectedRoute(route.routeId, route.datumISO ?? null)}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">
                  {route.dagNaam
                    ? `${route.dagNaam} ${route.dagNummer ?? ''} ${route.maandNaam ?? ''}`.trim()
                    : route.datum}
                </div>
                <div className="text-xs text-slate-500">{route.tijdslot}</div>
                {(isVandaag || isNietVoorstellen) && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {isVandaag && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        🚐 Chauffeur onderweg
                      </span>
                    )}
                    {isNietVoorstellen && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                        Niet voorstellen
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {isFullEffective ? (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                    Vol
                  </span>
                ) : (
                  <span
                    className={clsx(
                      'text-[10px] font-medium px-1.5 py-0.5 rounded',
                      isLowCapacity
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-50 text-emerald-700'
                    )}
                  >
                    {effectiveCapaciteit} plek{effectiveCapaciteit === 1 ? '' : 'ken'}
                  </span>
                )}
                {hasPending ? (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                    +{pendingBump.delta} toegevoegd
                  </span>
                ) : (
                  (isFullEffective || isLowCapacity) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        openCapacityBump(route.routeId)
                      }}
                      className={clsx(
                        'text-[10px] hover:underline',
                        isFullEffective ? 'text-amber-700 font-medium' : 'text-indigo-600'
                      )}
                    >
                      Capaciteit +
                    </button>
                  )
                )}
              </div>
            </label>
          )
        })}
      </div>

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={showMore}
          className="text-xs text-indigo-600 hover:underline"
        >
          Nog {nextStep} tonen{' '}
          <span className="text-slate-400">({hiddenCount} meer beschikbaar)</span>
        </button>
      )}
    </div>
  )
}
